import { DeleteOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Modal, Progress, Tooltip } from 'antd';
import { Button } from 'components/ui';
import dayjs, { Dayjs } from 'dayjs';
import { colors } from 'design';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  containerWrapper: {
    position: 'relative',
    maxHeight: '50vh',
  },
  container: {
    maxHeight: '50vh',
    overflowY: 'auto',
    paddingRight: 8,
    display: 'flex',
    flexDirection: 'column-reverse',
    scrollbarWidth: 'none',
  },
  fadeOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 50,
    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
    pointerEvents: 'none',
    zIndex: 10,
  },
  monthSection: {
    marginBottom: 8,
  },
  monthHeader: {
    top: 0,
    color: colors.text.primary,
    padding: '8px 0',
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  weekdayHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
    marginBottom: 4,
  },
  weekdayLabel: {
    fontSize: 14,
    color: colors.text.disabled,
    textTransform: 'capitalize',
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
  },
  cell: {
    background: colors.surface.page,
    border: `solid 2px ${colors.surface.page}`,
    aspectRatio: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontSize: 16,
    cursor: 'pointer',
    color: colors.text.tertiary,
    position: 'relative',
  },
  cellInMonth: {
    composes: '$cell',
    color: colors.text.primary,
  },
  cellSelected: {
    composes: '$cellInMonth',
    background: colors.feedback.error.bg,
    borderColor: colors.feedback.error.border,
  },
  cellSelectedEdge: {
    composes: '$cellSelected',
    borderColor: colors.dataViz.veryBad,
  },
  legend: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  // Range visualization styles
  rangeBase: {
    composes: '$cellInMonth',
    backgroundColor: colors.feedback.error.bg,
    borderColor: colors.dataViz.veryBad,
  },
  rangeSingle: {
    composes: '$rangeBase',
    borderRadius: 12,
  },
  rangeStart: {
    composes: '$rangeBase',
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
  },
  rangeMiddle: {
    composes: '$rangeBase',
  },
  rangeEnd: {
    composes: '$rangeBase',
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
  },
  pendingStart: {
    composes: '$rangeStart',
    outline: `2px dashed ${colors.dataViz.veryBad}`,
    outlineOffset: -4,
    border: 'none',
  },
  // Today and future day styles
  cellToday: {
    composes: '$cellInMonth',
    fontWeight: 'bold',
  },
  cellFuture: {
    composes: '$cell',
    color: colors.text.disabled, // lighter gray for future days
    cursor: 'not-allowed',
  },
  cellEmpty: {
    background: 'rgba(247, 247, 244, 0.3)',
    aspectRatio: 1,
  },
});

export type PeriodQuickFillModalProps = {
  open: boolean;
  onClose: () => void;
  onValidate: () => void;
};

export default function PeriodQuickFillModal({ open, onClose, onValidate }: PeriodQuickFillModalProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitScroll = useRef(false);
  const isLoadingMorePast = useRef(false);

  const scrollableTargetId = 'period-quickfill-scrollable';

  const { fillPeriodDays, removePeriodRange, getSpecificDailyStatus, cycleHistory } = useSymptomTrackingData();

  const [anchorMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [months, setMonths] = useState<Dayjs[]>([]);
  const [selection, setSelection] = useState<{ start?: Dayjs; end?: Dayjs }>({});
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const numberOfCycles = cycleHistory.length;
  const progress = Math.min(100, Math.round((numberOfCycles / 3) * 100));

  const weekdayLabels = useMemo(() => {
    const weekStart = dayjs().startOf('week');
    return Array.from({ length: 7 }).map((_, i) => weekStart.add(i, 'day').format('dd').slice(0, 2));
  }, []);

  const buildMonthDays = useCallback((monthStart: Dayjs): (Dayjs | null)[] => {
    const start = monthStart.startOf('month');
    const end = monthStart.endOf('month');

    // Leading/trailing blank cells align the 1st (and last) of the month to
    // its actual weekday column, without borrowing days from neighboring
    // months (which would show them under the wrong month header).
    const leadingBlanks = start.diff(start.startOf('week'), 'day');
    const trailingBlanks = end.endOf('week').diff(end, 'day');

    const days: (Dayjs | null)[] = new Array(leadingBlanks).fill(null);
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    days.push(...new Array(trailingBlanks).fill(null));

    return days;
  }, []);

  // Helpers to detect period ranges in existing data
  const isPeriodDay = useCallback(
    (d: Dayjs) => {
      const status = getSpecificDailyStatus(d);
      return Boolean(status && status.menstruation_amount > 0);
    },
    [getSpecificDailyStatus],
  );

  const getRangePosition = useCallback(
    (d: Dayjs): 'single' | 'start' | 'middle' | 'end' | undefined => {
      if (!isPeriodDay(d)) return undefined;
      const prev = d.subtract(1, 'day');
      const next = d.add(1, 'day');
      const prevIs = isPeriodDay(prev);
      const nextIs = isPeriodDay(next);
      if (!prevIs && !nextIs) return 'single';
      if (!prevIs && nextIs) return 'start';
      if (prevIs && nextIs) return 'middle';
      if (prevIs && !nextIs) return 'end';
      return undefined;
    },
    [isPeriodDay],
  );

  // Find and delete entire period range starting from any day in the range
  const deletePeriodRange = useCallback(
    (clickedDay: Dayjs) => {
      if (!isPeriodDay(clickedDay)) return;

      // Find the start of the range by going backwards
      let rangeStart = clickedDay;
      while (rangeStart.subtract(1, 'day').isValid() && isPeriodDay(rangeStart.subtract(1, 'day'))) {
        rangeStart = rangeStart.subtract(1, 'day');
      }

      // Find the end of the range by going forwards
      let rangeEnd = clickedDay;
      while (rangeEnd.add(1, 'day').isValid() && isPeriodDay(rangeEnd.add(1, 'day'))) {
        rangeEnd = rangeEnd.add(1, 'day');
      }

      // Remove all days in the range with a single request
      removePeriodRange(rangeStart, rangeEnd);

      // Hide tooltip after deletion and clear any pending selection
      setTooltipVisible(null);
      setSelection({});
    },
    [isPeriodDay, removePeriodRange],
  );

  // Initialize with several months, ordered oldest -> newest so current month is at the bottom
  useEffect(() => {
    const initial: Dayjs[] = [];
    for (let i = 0; i < 12; i += 1) {
      initial.push(anchorMonth.subtract(i, 'month'));
    }
    setMonths(initial.reverse());
  }, [anchorMonth]);

  // Keep latest month visible at bottom only once when opening
  useEffect(() => {
    if (open && !didInitScroll.current) {
      didInitScroll.current = true;
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
    if (!open) {
      didInitScroll.current = false;
    }
    // Always reset tooltip visibility and selection when modal open state changes
    setTooltipVisible(null);
    setSelection({});
  }, [open]);

  const loadMorePast = useCallback(() => {
    if (isLoadingMorePast.current) return;
    isLoadingMorePast.current = true;

    setMonths(prev => {
      const earliest = prev[0];
      const nexts: Dayjs[] = [];
      for (let i = 6; i >= 1; i -= 1) {
        nexts.push(earliest.subtract(i, 'month'));
      }
      return [...nexts, ...prev];
    });

    // allow another call on next scroll cycle
    requestAnimationFrame(() => {
      isLoadingMorePast.current = false;
    });
  }, []);

  const isSelected = useCallback(
    (d: Dayjs) => {
      if (!selection.start || !selection.end) return false;
      return d.isSameOrAfter(selection.start, 'day') && d.isSameOrBefore(selection.end, 'day');
    },
    [selection],
  );

  const onDayClick = useCallback(
    (d: Dayjs) => {
      const today = dayjs();
      if (d.isAfter(today, 'day')) return;

      const status = getSpecificDailyStatus(d);
      const isPeriod = status && status.menstruation_amount > 0;

      // Hide any visible tooltips when clicking
      setTooltipVisible(null);

      // If we have a pending selection (start but no end), treat any click as the end point
      if (selection.start && !selection.end) {
        let start = selection.start;
        let end = d;
        if (d.isBefore(start, 'day')) {
          start = d;
          end = selection.start;
        }
        setSelection({ start, end });
        // Apply selection as period days
        const length = end.diff(start, 'day') + 1;
        fillPeriodDays(start, length);
        posthog.capture('tracking.period_quickfilled_one_cycle');
        return;
      }

      // If clicking on an existing period day and no pending selection, show tooltip for deletion
      if (isPeriod) {
        const dayKey = d.format('YYYY-MM-DD');
        setTooltipVisible(tooltipVisible === dayKey ? null : dayKey);
        return;
      }

      // Selection mode: first click sets start
      if (!selection.start || (selection.start && selection.end)) {
        setSelection({ start: d, end: undefined });
        posthog.capture('tracking.period_quickfill_started');
        return;
      }
    },
    [selection, getSpecificDailyStatus, fillPeriodDays, tooltipVisible],
  );

  const handleClose = useCallback(() => {
    setTooltipVisible(null);
    setSelection({});
    onClose();
  }, [onClose]);

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
      <Button variant="link" onClick={handleClose}>
        {t('common.later', 'Plus tard')}
      </Button>
      <Button variant="primary" onClick={onValidate} disabled={numberOfCycles < 3}>
        {t('symptomTracking.cycles.validate', 'Validate')}
      </Button>
    </div>
  );

  const monthsForRender = [...months].reverse(); // render newest first; column-reverse will place it at bottom

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={t('symptomTracking.cycles.fillPastCycles', 'Renseigner mes cycles')}
      footer={footer}
      width={400}
    >
      <div className={styles.containerWrapper}>
        <div className={styles.fadeOverlay} />
        <div id={scrollableTargetId} ref={scrollRef} className={styles.container}>
          <InfiniteScroll
            height={1000}
            dataLength={months.length}
            next={loadMorePast}
            hasMore={true}
            inverse
            loader={<div style={{ textAlign: 'center' }}>Loading...</div>}
            style={{ display: 'flex', flexDirection: 'column-reverse', scrollbarWidth: 'none' }}
            scrollableTarget={scrollableTargetId}
          >
            {monthsForRender.map((m, idx) => {
              const days = buildMonthDays(m);
              const monthKey = m.format('YYYY-MM');
              return (
                <div key={`${monthKey}-${idx}`} className={styles.monthSection} data-month={monthKey}>
                  <div className={styles.monthHeader}>{m.format('MMMM YYYY')}</div>
                  <div className={styles.weekdayHeader}>
                    {weekdayLabels.map((label, i) => (
                      <div key={`weekday-${monthKey}-${i}`} className={styles.weekdayLabel}>
                        {label}
                      </div>
                    ))}
                  </div>
                  <div className={styles.grid}>
                    {days.map((d, i) => {
                      if (!d) {
                        return <div key={`blank-${monthKey}-${i}`} className={styles.cellEmpty} />;
                      }

                      const today = dayjs();
                      const isToday = d.isSame(today, 'day');
                      const isFuture = d.isAfter(today, 'day');

                      // Determine base class based on day type
                      let baseCls = styles.cellInMonth;
                      if (isFuture) {
                        baseCls = styles.cellFuture;
                      } else if (isToday) {
                        baseCls = styles.cellToday;
                      }

                      const sel = isSelected(d);
                      const isEdge = sel && (d.isSame(selection.start, 'day') || d.isSame(selection.end, 'day'));
                      let className = isEdge ? styles.cellSelectedEdge : sel ? styles.cellSelected : baseCls;

                      // visualize existing ranges
                      const pos = getRangePosition(d);
                      if (pos === 'single') className = `${className} ${styles.rangeSingle}`;
                      else if (pos === 'start') className = `${className} ${styles.rangeStart}`;
                      else if (pos === 'middle') className = `${className} ${styles.rangeMiddle}`;
                      else if (pos === 'end') className = `${className} ${styles.rangeEnd}`;

                      // pending start
                      if (!selection.end && selection.start && d.isSame(selection.start, 'day')) {
                        className = `${className} ${styles.pendingStart}`;
                      }

                      return (
                        <Tooltip
                          key={`tooltip-${d.format('YYYY-MM-DD')}-${i}`}
                          title={
                            isPeriodDay(d) ? (
                              <div
                                style={{
                                  cursor: 'pointer',
                                  color: 'white',
                                  marginTop: 3,
                                }}
                                onClick={e => {
                                  e.stopPropagation();
                                  deletePeriodRange(d);
                                }}
                              >
                                <DeleteOutlined style={{ marginRight: 8 }} />
                                {t('symptomTracking.cycles.deleteCycle')}
                              </div>
                            ) : null
                          }
                          open={isPeriodDay(d) && tooltipVisible === d.format('YYYY-MM-DD')}
                          placement="top"
                          getPopupContainer={trigger =>
                            (trigger.closest('.ant-modal-body') as HTMLElement) || document.body
                          }
                        >
                          <div
                            key={`${d.format('YYYY-MM-DD')}-${i}`}
                            data-testid={`quickfill-${d.format('YYYY-MM-DD')}`}
                            data-selected={sel}
                            className={className}
                            onClick={() => onDayClick(d)}
                          >
                            {d.date()}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 12 }}>
        <InfoCircleFilled /> {t('symptomTracking.cycles.fillAtLeastThreeCycles')}
      </div>
      <div className={styles.legend}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <Progress percent={progress} showInfo format={() => `${numberOfCycles}/3`} />
        </div>
      </div>
    </Modal>
  );
}
